/**
 * Vue Tendances — CDC §3.4.
 *
 * Sélecteur de métrique et de période, graphique d'évolution, narration de
 * tendance en langage naturel, et bilan hebdomadaire (3 réussites / 2 points
 * d'attention / 1 micro-objectif).
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { santeApi } from '../api/santeApi'
import {
  EmptyState,
  HEALTH_ACCENT,
  Loading,
  SectionTitle,
  cardSx,
  darkFieldSx,
  metricLabel,
} from './shared'

const METRICS = [
  'steps',
  'sleep_duration',
  'weight',
  'resting_heart_rate',
  'systolic_bp',
  'glucose',
  'mood',
  'hydration',
]
const PERIODS = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: 365, label: '1 an' },
]

const TREND_TEXT = {
  hausse: 'À la hausse sur la période.',
  baisse: 'En baisse sur la période.',
  plateau: 'Stable — un plateau sur la période.',
}

export default function TrendsView() {
  const [metric, setMetric] = useState('steps')
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [review, setReview] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await santeApi.getTrends(metric, days)
    setData(res)
    setLoading(false)
  }, [metric, days])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    santeApi.weeklyReviews.list().then((rows) => setReview(rows[0] || null))
  }, [])

  const generateReview = async () => {
    const r = await santeApi.generateWeeklyReview()
    setReview(r)
  }

  const chartData = (data?.series || []).map((p) => ({
    date: new Date(p.recorded_at).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
    }),
    value: p.value,
  }))

  return (
    <Box>
      <Box sx={{ ...cardSx, mb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Métrique"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            sx={{ minWidth: 200, ...darkFieldSx }}
          >
            {METRICS.map((m) => (
              <MenuItem key={m} value={m}>
                {metricLabel(m)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Période"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ minWidth: 140, ...darkFieldSx }}
          >
            {PERIODS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {loading ? (
          <Loading label="Chargement de la série…" />
        ) : chartData.length === 0 ? (
          <EmptyState>
            Aucune donnée « {metricLabel(metric)} » sur cette période.
          </EmptyState>
        ) : (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.45)" fontSize={11} />
                <YAxis stroke="rgba(255,255,255,0.45)" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: '#0c0c3a',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 8,
                    color: '#fff',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={HEALTH_ACCENT}
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {data?.trend && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(61,220,151,0.08)',
            }}
          >
            <Typography sx={{ color: HEALTH_ACCENT, fontWeight: 700, fontSize: 13 }}>
              Tendance détectée
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
              {TREND_TEXT[data.trend.trend] || 'Tendance analysée.'} (
              {data.trend.points} points, de {data.trend.first} à{' '}
              {data.trend.last})
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bilan hebdomadaire */}
      <SectionTitle
        action={
          <Button
            size="small"
            onClick={generateReview}
            sx={{ color: HEALTH_ACCENT, textTransform: 'none' }}
          >
            Générer
          </Button>
        }
      >
        Bilan hebdomadaire
      </SectionTitle>
      {!review ? (
        <EmptyState>
          Aucun bilan pour l’instant. Lancez la génération pour obtenir votre
          synthèse de la semaine.
        </EmptyState>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ ...cardSx, height: '100%' }}>
              <Typography sx={{ color: HEALTH_ACCENT, fontWeight: 700, mb: 1 }}>
                Réussites
              </Typography>
              {(review.successes || []).map((s, i) => (
                <Typography
                  key={i}
                  sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, mb: 0.5 }}
                >
                  • {s}
                </Typography>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ ...cardSx, height: '100%' }}>
              <Typography sx={{ color: '#ffb454', fontWeight: 700, mb: 1 }}>
                Points d’attention
              </Typography>
              {(review.attention_points || []).map((s, i) => (
                <Typography
                  key={i}
                  sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, mb: 0.5 }}
                >
                  • {s}
                </Typography>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ ...cardSx, height: '100%' }}>
              <Typography sx={{ color: '#5ab9ff', fontWeight: 700, mb: 1 }}>
                Micro-objectif
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                {review.micro_goal}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
